/**
 * Time formatting utilities for consistent display across the application
 * All input values should be in seconds
 */

export interface TimeFormatOptions {
  showSeconds?: boolean;
  short?: boolean;
  verbose?: boolean;
}

/**
 * Formats time duration in seconds to human readable format
 * @param seconds - Duration in seconds
 * @param options - Formatting options
 * @returns Formatted time string
 */
export const formatTime = (seconds: number, options: TimeFormatOptions = {}): string => {
  const { showSeconds = false, short = true, verbose = false } = options;

  // Handle invalid inputs
  if (typeof seconds !== 'number' || seconds < 0 || !isFinite(seconds)) {
    return '0s';
  }

  // Handle legacy data where duration might have been incorrectly stored in milliseconds
  // If the value is very large (> 24 hours in seconds), it might be in milliseconds
  let timeInSeconds = seconds;
  if (seconds > 86400) { // More than 24 hours suggests milliseconds
    timeInSeconds = Math.round(seconds / 1000);
  }

  const hours = Math.floor(timeInSeconds / 3600);
  const minutes = Math.floor((timeInSeconds % 3600) / 60);
  const secs = Math.floor(timeInSeconds % 60);

  if (verbose) {
    const parts = [];
    if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hora' : 'horas'}`);
    if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`);
    if (showSeconds && secs > 0) parts.push(`${secs} ${secs === 1 ? 'segundo' : 'segundos'}`);
    return parts.join(', ') || '0 segundos';
  }

  if (short) {
    if (hours > 0) {
      if (minutes > 0) {
        return showSeconds ? `${hours}h ${minutes}m ${secs}s` : `${hours}h ${minutes}m`;
      }
      return `${hours}h`;
    }
    if (minutes > 0) {
      return showSeconds ? `${minutes}m ${secs}s` : `${minutes}m`;
    }
    return `${secs}s`;
  }

  // Default format
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}${showSeconds ? `:${secs.toString().padStart(2, '0')}` : ''}`;
  }
  if (minutes > 0) {
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }
  return `0:${secs.toString().padStart(2, '0')}`;
};

/**
 * Formats study time for statistics display
 * @param seconds - Duration in seconds
 * @returns User-friendly time string
 */
export const formatStudyTime = (seconds: number): string => {
  return formatTime(seconds, { short: true });
};

/**
 * Formats time for detailed statistics
 * @param seconds - Duration in seconds
 * @returns Verbose time string
 */
export const formatDetailedTime = (seconds: number): string => {
  return formatTime(seconds, { verbose: true, showSeconds: true });
};

/**
 * Formats time for session display
 * @param seconds - Duration in seconds
 * @returns Time string with hours:minutes:seconds format
 */
export const formatSessionTime = (seconds: number): string => {
  return formatTime(seconds, { short: false, showSeconds: true });
};

/**
 * Calculates time from Date.now() timestamp to seconds
 * @param startTime - Start timestamp from Date.now()
 * @param endTime - End timestamp from Date.now() (optional, defaults to now)
 * @returns Duration in seconds
 */
export const calculateDurationInSeconds = (startTime: number, endTime?: number): number => {
  const end = endTime || Date.now();
  return Math.round((end - startTime) / 1000);
};

/**
 * Formats monthly study time for subscription display
 * @param seconds - Duration in seconds
 * @returns Time string using consistent s/m/h format
 */
export const formatMonthlyStudyTime = (seconds: number): string => {
  return formatTime(seconds, { short: true });
};

/**
 * Converts milliseconds to seconds (for legacy data compatibility)
 * @param milliseconds - Time in milliseconds
 * @returns Time in seconds
 */
export const msToSeconds = (milliseconds: number): number => {
  return Math.round(milliseconds / 1000);
};

/**
 * Validates if a time value is in seconds or milliseconds and returns seconds
 * @param timeValue - Time value that might be in seconds or milliseconds
 * @returns Time in seconds
 */
export const normalizeTimeToSeconds = (timeValue: number): number => {
  // If value is very large (> 1 hour in seconds), assume it's in milliseconds
  if (timeValue > 3600) {
    return msToSeconds(timeValue);
  }
  return timeValue;
};
